/**
 * CommunicationsTab Component
 * Displays communication timeline (emails and notes) for an organization
 */
import React, { memo, useState, useMemo, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { authFetch } from '../../lib/api';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../ui/select';
import { 
  Mail, 
  Phone, 
  Users, 
  StickyNote, 
  PenLine, 
  Plus, 
  Loader2, 
  Trash2,
  Eye,
  MoveRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../ui/dialog';

// Renders note/email content. For long notes, shows a 3-line preview with
// "Show more" toggle so users can expand the full multi-paragraph text.
// Email content stays clamped because emails have their own "View Full Email" dialog.
const NoteContent = memo(({ content, type }) => {
  const [expanded, setExpanded] = useState(false);
  const text = content || '';
  // Heuristic: a note is "long" if it has multiple paragraphs OR > 200 chars OR > 3 lines.
  const lineCount = text.split('\n').length;
  const isLong = type !== 'email' && (text.length > 200 || lineCount > 3);

  if (!isLong) {
    return <p className="text-sm whitespace-pre-wrap">{text}</p>;
  }

  return (
    <div>
      <p
        className={cn(
          "text-sm whitespace-pre-wrap",
          !expanded && "line-clamp-3"
        )}
        data-testid="note-content"
      >
        {text}
      </p>
      <Button
        variant="link"
        size="sm"
        className="px-0 h-6 text-xs mt-1"
        onClick={() => setExpanded(e => !e)}
        data-testid="note-content-toggle"
      >
        {expanded ? (
          <><ChevronUp className="w-3 h-3 mr-1" /> Show less</>
        ) : (
          <><ChevronDown className="w-3 h-3 mr-1" /> Show more</>
        )}
      </Button>
    </div>
  );
});
NoteContent.displayName = 'NoteContent';

export const CommunicationsTab = memo(({
  communications,
  communicationsLoading,
  newCommType,
  setNewCommType,
  newCommNote,
  setNewCommNote,
  addingComm,
  addCommunicationNote,
  deleteCommunicationNote,
  openEmailDialog,
  viewingOrg,
  formatDate,
  formatTime,
  moveImportedNote,
  allOrgs
}) => {
  const [viewingEmail, setViewingEmail] = React.useState(null);
  const [movingNote, setMovingNote] = useState(null); // { note_id, content, from_org_name }
  const [moveTargetSearch, setMoveTargetSearch] = useState('');
  const [moveTargetOrgId, setMoveTargetOrgId] = useState('');
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  // Server-side search results so the dialog can find ANY organisation in the
  // database, not just the currently-paginated page passed via `allOrgs`.
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Debounced server-side search across ALL organisations whenever the dialog
  // is open. Falls back to the locally-loaded `allOrgs` page when the user
  // hasn't typed anything.
  useEffect(() => {
    if (!movingNote) return;
    const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
    const q = moveTargetSearch.trim();
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const params = new URLSearchParams({ limit: '50', page: '1' });
        if (q) params.append('search', q);
        const res = await authFetch(`${API}/organizations?${params.toString()}`);
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        setSearchResults(Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []));
      } catch (_e) {
        // swallow — UI will fall back to empty results / "No matches"
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, q ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [movingNote, moveTargetSearch]);

  const filteredOrgs = useMemo(() => {
    const list = Array.isArray(searchResults) && searchResults.length > 0
      ? searchResults
      : (Array.isArray(allOrgs) ? allOrgs : []);
    if (!viewingOrg) return [];
    return list.filter(o => o.org_id !== viewingOrg.org_id).slice(0, 50);
  }, [searchResults, allOrgs, viewingOrg]);

  const openMoveDialog = (comm) => {
    setMovingNote({
      note_id: comm.id,
      content: comm.content,
      date: comm.date,
    });
    setMoveTargetSearch('');
    setMoveTargetOrgId('');
  };

  const submitMove = async () => {
    if (!movingNote || !moveTargetOrgId || !moveImportedNote) return;
    setMoveSubmitting(true);
    const ok = await moveImportedNote(movingNote.note_id, moveTargetOrgId);
    setMoveSubmitting(false);
    if (ok) {
      setMovingNote(null);
      setMoveTargetSearch('');
      setMoveTargetOrgId('');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Add New Communication Note */}
      <div className="p-4 border rounded-lg bg-muted/30">
        <h4 className="font-medium mb-3 flex items-center gap-2">
          <PenLine className="w-4 h-4" />
          Add Communication Note
        </h4>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Select value={newCommType} onValueChange={setNewCommType}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="phone_call">Phone Call</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              value={newCommNote}
              onChange={(e) => setNewCommNote(e.target.value)}
              placeholder="Enter communication details..."
              className="flex-1"
              rows={2}
            />
          </div>
          <div className="flex justify-end">
            <Button 
              size="sm" 
              onClick={addCommunicationNote}
              disabled={addingComm || !newCommNote.trim()}
            >
              {addingComm ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
              Add Note
            </Button>
          </div>
        </div>
      </div>

      {/* Communications Timeline */}
      {communicationsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : communications.length > 0 ? (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {communications.map(comm => (
            <div key={comm.id} className={cn(
              "p-4 border rounded-lg",
              comm.type === 'email' ? "bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800" : "bg-muted/30"
            )}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {comm.type === 'email' ? (
                      <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                        <Mail className="w-3 h-3 mr-1" />
                        Email
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="capitalize">
                        {comm.note_type === 'phone_call' ? (
                          <><Phone className="w-3 h-3 mr-1" />Phone Call</>
                        ) : comm.note_type === 'meeting' ? (
                          <><Users className="w-3 h-3 mr-1" />Meeting</>
                        ) : (
                          <><StickyNote className="w-3 h-3 mr-1" />{comm.note_type}</>
                        )}
                      </Badge>
                    )}
                    {comm.imported_from && (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-700">
                        Imported
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {formatDate(comm.date)} {formatTime(comm.date)}
                    </span>
                  </div>
                  {comm.type === 'email' && (
                    <div className="mb-2">
                      <p className="font-medium">{comm.subject}</p>
                      <p className="text-sm text-muted-foreground">To: {comm.to_email}</p>
                    </div>
                  )}
                  <NoteContent content={comm.content} type={comm.type} />
                  {comm.type === 'email' && (
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="px-0 h-6 text-xs mt-1"
                      onClick={() => setViewingEmail(comm)}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Full Email
                    </Button>
                  )}
                </div>
                <div className="text-right shrink-0 flex flex-col items-end gap-1">
                  <p className="text-sm font-medium">{comm.user_name}</p>
                  {comm.type === 'note' && comm.imported_from && moveImportedNote && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => openMoveDialog(comm)}
                      data-testid={`move-note-btn-${comm.id}`}
                      title="Move this note to another organisation"
                    >
                      <MoveRight className="w-3 h-3 mr-1" />
                      Move
                    </Button>
                  )}
                  {comm.type === 'note' && !comm.imported_from && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-destructive hover:text-destructive"
                      onClick={() => deleteCommunicationNote(comm.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground border rounded-lg">
          <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No communications yet</p>
          <p className="text-sm">Add a note or send an email to get started</p>
          <Button variant="link" onClick={() => openEmailDialog(viewingOrg)}>
            Send an email
          </Button>
        </div>
      )}

      {/* Email Detail Dialog */}
      <Dialog open={!!viewingEmail} onOpenChange={(open) => !open && setViewingEmail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
          </DialogHeader>
          {viewingEmail && (
            <div className="space-y-4">
              <div className="grid grid-cols-[80px_1fr] gap-2 text-sm">
                <span className="text-muted-foreground font-medium">To:</span>
                <span>{viewingEmail.to_email}</span>
                <span className="text-muted-foreground font-medium">Subject:</span>
                <span className="font-medium">{viewingEmail.subject}</span>
                <span className="text-muted-foreground font-medium">Sent:</span>
                <span>{formatDate(viewingEmail.date)} {formatTime(viewingEmail.date)}</span>
                <span className="text-muted-foreground font-medium">By:</span>
                <span>{viewingEmail.user_name}</span>
              </div>
              <div className="border-t pt-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Message Body:</p>
                <div className="p-4 bg-muted/30 rounded-lg text-sm whitespace-pre-wrap max-h-[400px] overflow-y-auto">
                  {viewingEmail.content}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Move Note Dialog */}
      <Dialog open={!!movingNote} onOpenChange={(open) => !open && setMovingNote(null)}>
        <DialogContent className="max-w-lg" data-testid="move-note-dialog">
          <DialogHeader>
            <DialogTitle>Move Note to Another Organisation</DialogTitle>
            <DialogDescription>
              Pick the correct organisation. The note will be removed from the current one and added to the selected one.
            </DialogDescription>
          </DialogHeader>
          {movingNote && (
            <div className="space-y-3">
              <div className="p-3 rounded border bg-muted/30 text-sm">
                <p className="text-xs text-muted-foreground mb-1">{movingNote.date}</p>
                <p className="line-clamp-3 whitespace-pre-wrap">{movingNote.content}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Search target organisation</label>
                <Input
                  placeholder="Type at least 1 character to search all organisations…"
                  value={moveTargetSearch}
                  onChange={(e) => setMoveTargetSearch(e.target.value)}
                  autoFocus
                  data-testid="move-note-search-input"
                />
                <div className="max-h-72 overflow-y-auto border rounded">
                  {searchLoading ? (
                    <div className="p-3 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                    </div>
                  ) : filteredOrgs.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No matches</div>
                  ) : (
                    filteredOrgs.map(o => (
                      <button
                        key={o.org_id}
                        type="button"
                        onClick={() => setMoveTargetOrgId(o.org_id)}
                        className={cn(
                          "w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-muted/60",
                          moveTargetOrgId === o.org_id && "bg-primary/10 font-medium"
                        )}
                        data-testid={`move-note-target-${o.org_id}`}
                      >
                        {o.name}
                      </button>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Showing top {filteredOrgs.length} match{filteredOrgs.length === 1 ? '' : 'es'}. Refine your search if you don&apos;t see the right one.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovingNote(null)}>Cancel</Button>
            <Button
              onClick={submitMove}
              disabled={!moveTargetOrgId || moveSubmitting}
              data-testid="move-note-submit-btn"
            >
              {moveSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Move Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

CommunicationsTab.displayName = 'CommunicationsTab';

export default CommunicationsTab;
